// ─── Shared ribbon primitives ───────────────────────────────────────────────
//
// Before this file existed, OutlookWorkspace.js and MailMerge.js each hand-
// rolled their own nearly-identical <RBtn>/<RGroup> (ribbon button / ribbon
// command group) and OutlookWorkspace.js alone had <RibbonTabButton> (the
// Home/Send/Folder/... tab strip) and <RibbonOverflowRow> (the responsive
// ">>" overflow behavior for a ribbon tab's row of groups when the window is
// too narrow to show them all). Two consequences of that duplication, found
// while consolidating them here:
//
//  1. MailMerge's <RBtn> never got wired onto the shared color tokens in
//     workspaceTheme.js — its hover/active backgrounds were hardcoded to the
//     old Microsoft Fluent hex values ("#e1dfdd"/"#c7e0f4") and its <RGroup>
//     divider was hardcoded to "#d1cec9", so regardless of which palette
//     workspaceTheme.js is set to (Outlook-blue, Bordeaux, Lightning, ...),
//     those specific pixels have been silently stuck on the original
//     Microsoft colors this whole time. That's fixed here — both now read
//     from C.ribbonHover/C.ribbonActive/C.accent/C.ribbonBorder like
//     Workspace's always did, so Mail Merge's ribbon tracks the palette
//     correctly like the rest of the app.
//  2. The two files' <RBtn> handle the `icon` prop differently: Workspace
//     treats a string icon as a *name* to look up in RibbonIcons.js's
//     dictionary (icon="archive"); Mail Merge passes renderable content
//     straight through, INCLUDING literal short text/glyph strings it uses
//     for its own formatting buttons (icon="B", icon="H1", icon="#") that
//     aren't in that dictionary at all. resolveIcon() below reconciles both:
//     non-string icons (already an element/fragment) pass through unchanged;
//     string icons that exist in RibbonIcons.js's ICON_NAMES resolve to a
//     named <RIcon>; any other string (including "") renders as literal
//     text, exactly like Mail Merge's own icon prop always has. This is a
//     superset of both files' previous behavior, not a new convention either
//     had to adopt.
//
// Sizing (large/default/sm padding, minWidth, gap) matches each file's own
// prior numbers depending on which prop is passed — those were each file's
// own considered spacing choice, not a bug, so they're preserved as options
// on one component rather than forced to match each other.
import { useState, useRef, useLayoutEffect, cloneElement } from "react";
import { createPortal } from "react-dom";
import { RIcon, ICON_NAMES } from "../RibbonIcons";
import { WORKSPACE_THEME, STATUS_CHIP_COLORS, WORKSPACE_RADIUS } from "../workspaceTheme";

const C = WORKSPACE_THEME;
const R = WORKSPACE_RADIUS;

// One status-chip look, reused by Workspace's connection indicator, Mail
// Merge's run-status badge, and Sequence's Monitor view — colors come from
// STATUS_CHIP_COLORS (workspaceTheme.js); each caller supplies its own
// `bucket` (one of that map's keys) and its own human-readable `label`,
// since the underlying status vocabulary genuinely differs per domain.
export function StatusChip({bucket,label,sm}){
  const colors=STATUS_CHIP_COLORS[bucket]||STATUS_CHIP_COLORS.stopped;
  return(
    <span style={{background:colors.bg,color:colors.tc,fontSize:sm?10:11,padding:sm?"2px 7px":"3px 9px",borderRadius:99,fontWeight:600,whiteSpace:"nowrap",display:"inline-block"}}>
      {label}
    </span>
  );
}

function resolveIcon(icon, size, color){
  if(icon==null || icon==="") return icon; // nothing to render — matches both files' prior behavior for an empty icon
  if(typeof icon!=="string") return icon;  // already a React element/fragment (e.g. <RIcon .../>, <>&#8644;</>) — pass through
  if(ICON_NAMES.has(icon)) return <RIcon name={icon} size={size} color={color}/>;
  return icon; // literal text/glyph (e.g. "B", "H1", "#", a merge-tag preview) — render as-is
}

// `dense` reproduces MailMerge's own original, slightly tighter metrics
// (minWidth/padding/gap/radius/no border-ring/plain label color) instead of
// Workspace's — a real width difference, not a rounding detail: without it,
// MailMerge's ribbon groups render a few pixels wider per button, and in at
// least one group (Rotation & Testing) that was enough to push its caption
// from one line to two. MailMerge.js's own RGroup/RBtn wrapper passes this
// automatically, so none of its call sites needed to change.
export function RBtn({icon,label,onClick,disabled,active,large,sm,danger,title,dense}){
  const [h,setH]=useState(false);
  const iconSize = large?(dense?20:21):sm?12:(dense?15:16);
  const iconColor = danger?C.red:active?C.accent:C.textSub;
  const iconEl = resolveIcon(icon, iconSize, iconColor);
  const minWidth = large?(dense?52:56):sm?28:(dense?32:36);
  const padding = large?(dense?"6px 10px":"6px 11px"):sm?"2px 6px":(dense?"3px 6px":"4px 7px");
  const labelColor = danger?C.red:dense?C.textSub:(active?C.accent:C.textSub);
  // Rounder, softer corners (R.sm/R.md instead of the original flat 2/4px)
  // plus a light lift-on-hover shadow — the ribbon's "professional/rounded"
  // pass. Radius scales with density so dense (Mail Merge-style) buttons
  // don't look disproportionately round next to their tighter padding.
  const hovering=h&&!disabled&&!active;
  return(
    <button onClick={onClick} disabled={disabled}
      onMouseEnter={()=>setH(true)} onMouseLeave={()=>setH(false)}
      title={title||label}
      style={{background:active?C.ribbonActive:hovering?C.ribbonHover:"transparent",border:dense?"none":"1px solid "+(active?C.accent+"66":"transparent"),borderRadius:dense?R.sm:R.md,padding,cursor:disabled?"not-allowed":"pointer",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:dense?1:3,minWidth,opacity:disabled?.4:1,boxShadow:hovering?"0 1px 4px rgba(0,0,0,.08)":"none",transition:"background .12s,border-color .12s,box-shadow .12s",fontFamily:"'Segoe UI',sans-serif",color:danger?C.red:C.text}}>
      <span style={{display:"flex",alignItems:"center",justifyContent:"center",height:iconSize,fontSize:sm?12:undefined,lineHeight:1}}>{iconEl}</span>
      {label&&<span style={{fontSize:dense?9:9.5,color:labelColor,fontWeight:!dense&&active?600:400,whiteSpace:"nowrap",textAlign:"center",lineHeight:1.2}}>{label}</span>}
    </button>
  );
}

// Top ribbon tab (Home/Send/Folder/View/Help + the special File "backstage"
// tab). Carries its own hover state. `theme` is optional (defaults to the
// module-level WORKSPACE_THEME, Workspace/Mail Merge's existing behavior,
// unchanged for every pre-existing caller) — added so Sequence's stage
// tracker (Build/Configure/Review/Activate/Monitor) can reuse this same
// rounded tab-button look while rendering in SEQUENCE_THEME's own palette,
// the same theme-agnostic pattern <SelectionStateBar> above already uses
// for the same reason. `title` is likewise optional and purely additive —
// an HTML tooltip, nothing that changes existing markup/behavior.
export function RibbonTabButton({label,isFile,isActive,onClick,theme,title}){
  const [h,setH]=useState(false);
  const C2=theme||C;
  return(
    <button onClick={onClick} onMouseEnter={()=>setH(true)} onMouseLeave={()=>setH(false)} title={title}
      style={{
        padding:"7px 18px",border:"none",borderRadius:R.md+"px "+R.md+"px 0 0",
        background:isFile?C2.accent:isActive?"#fff":h?"rgba(0,0,0,.05)":"transparent",
        borderBottom:isActive&&!isFile?"2.5px solid "+C2.accent:"2.5px solid transparent",
        boxShadow:isActive&&!isFile?"0 -2px 8px rgba(0,0,0,.05)":"none",
        color:isFile?"#fff":isActive?C2.accent:C2.text,
        fontSize:12,cursor:"pointer",fontWeight:isFile||isActive?600:400,
        fontFamily:"'Segoe UI',sans-serif",marginBottom:-1,transition:"background .12s,box-shadow .12s"}}>
      {label}
    </button>
  );
}

// Portal-positioned dropdown menu for ribbon buttons (New Items, Move,
// Categorize, Schedule, ...). Found while giving the ribbon its rounded/
// professional pass: every one of these menus was previously a plain
// `position:"absolute"` child sitting inside the ribbon strip's own
// `overflowX:"auto"` container — which, per the CSS overflow spec, forces
// overflow-y to compute to "auto" too even though only X was set. That
// silently clipped these menus down to roughly one visible row (verified
// via screenshot: the New Items menu showed only its first item, "Email
// Message", with the rest cut off) — pre-existing on every ribbon build,
// not something the rounded-corners pass introduced, just a lot more
// noticeable now that the menu has a visible border+shadow framing an
// obviously-truncated box. <RibbonOverflowRow>'s own "»" flyout already
// solved this exact problem with a portal straight into document.body, so
// this generalizes that same fix for the four simpler always-open-below
// ribbon dropdowns instead of leaving them clipped. `anchorRef` is the
// trigger's own wrapping element (the ribbon button or its relative-
// positioned wrapper div); position is computed in viewport ("fixed")
// terms from its on-screen rect, recomputed on resize/scroll while open.
export function RibbonMenu({open,anchorRef,onClose,children,minWidth=150,maxHeight=320}){
  const [pos,setPos]=useState(null);
  useLayoutEffect(()=>{
    if(!open){setPos(null);return;}
    const recompute=()=>{
      const el=anchorRef.current;
      if(!el)return;
      const r=el.getBoundingClientRect();
      setPos({top:r.bottom+4,left:r.left});
    };
    recompute();
    window.addEventListener("resize",recompute);
    window.addEventListener("scroll",recompute,true);
    return()=>{window.removeEventListener("resize",recompute);window.removeEventListener("scroll",recompute,true);};
  },[open,anchorRef]);
  if(!open||!pos)return null;
  return createPortal(
    <>
      <div onClick={onClose} style={{position:"fixed",inset:0,zIndex:100000}}/>
      <div onClick={e=>e.stopPropagation()} style={{position:"fixed",top:pos.top,left:pos.left,zIndex:100001,background:"#fff",border:"1px solid "+C.ribbonBorder,borderRadius:R.md,boxShadow:"0 6px 20px rgba(0,0,0,.14)",minWidth,maxHeight,overflowY:"auto",padding:4,fontFamily:"'Segoe UI',sans-serif"}}>
        {children}
      </div>
    </>,
    document.body
  );
}

// Generalizes the anchor-ref + open-state + <RibbonMenu> scaffolding that
// New Items / Move / Categorize / Schedule (Workspace) and Insert Merge
// Field / Categorize / Schedule (Mail Merge) each hand-assembled
// independently — same three lines (`<div ref><RBtn onClick=.../>
// <RibbonMenu open anchorRef onClose>`) copied at 7 call sites. This
// component owns only that scaffolding, not the trigger's appearance or the
// menu's contents, since the two files' trigger buttons are genuinely
// different (Workspace's are plain <RBtn>; Mail Merge's are custom-styled
// buttons with their own icon+label layout) — forcing one trigger style
// would mean re-skinning working buttons for zero benefit.
//
// `trigger` is a render-prop `({open,toggle,close}) => element` so any
// existing trigger button (RBtn or a bespoke one) just swaps its own
// `onClick={()=>setShowXMenu(v=>!v)}` for `onClick={toggle}`, unchanged
// otherwise. `children` may likewise be a render-prop `(close) => element`
// so menu items can call `close()` instead of reaching back into a
// parent-scoped `setShowXMenu(false)` — or plain children, since several
// existing menus already close themselves item-by-item and don't need it.
//
// Open state is normally internal (each dropdown manages its own boolean),
// but `openState={[open,setOpen]}` lets a caller keep owning the state
// externally — needed for "Move," whose menu body branches on a second,
// sibling piece of state (`showMoveOtherFolders`) that must survive this
// wrapper, and for any pair of menus (Move/Categorize) that close each
// other via their own setters in `onOpen`.
export function RibbonDropdownButton({trigger,children,minWidth,maxHeight,onOpen,openState}){
  const internalState=useState(false);
  const [open,setOpen]=openState||internalState;
  const anchorRef=useRef(null);
  const toggle=()=>setOpen(v=>{
    const next=!v;
    if(next&&onOpen)onOpen();
    return next;
  });
  const close=()=>setOpen(false);
  return(
    <div ref={anchorRef} style={{position:"relative"}}>
      {typeof trigger==="function"?trigger({open,toggle,close}):trigger}
      <RibbonMenu open={open} anchorRef={anchorRef} onClose={close} minWidth={minWidth} maxHeight={maxHeight}>
        {typeof children==="function"?children(close):children}
      </RibbonMenu>
    </div>
  );
}

export function RGroup({label,children,dense}){
  // Softer, lower-contrast group divider (was a flat 1px full-strength
  // border) — reads as a gentle separation between rounded button clusters
  // rather than a hard ruled line, matching the rest of the "professional/
  // rounded" ribbon pass.
  return(
    <div style={{display:"flex",flexDirection:"column",padding:dense?"0 7px":"3px 9px 0",borderRight:"1px solid "+C.ribbonBorder+"99",minWidth:dense?40:42}}>
      <div style={{display:"flex",gap:dense?1:2,alignItems:"flex-start",flex:1,paddingBottom:3,flexWrap:"wrap"}}>{children}</div>
      <p style={{fontSize:dense?9:9.5,color:C.textMuted,textAlign:"center",borderTop:"1px solid "+C.ribbonBorder+"99",paddingTop:dense?2:3,paddingBottom:2,fontFamily:"'Segoe UI',sans-serif",whiteSpace:"nowrap",letterSpacing:".2px"}}>{label}</p>
    </div>
  );
}

// Generalizes Workspace's mail-only bulk-action bar ("N selected · Move ·
// Delete · ...") into something Sequence's Monitor view and sequence list
// can reuse too. Unlike the other primitives in this file, this one takes
// an explicit `theme` prop rather than reading the module-level
// WORKSPACE_THEME constant directly — Sequence renders in SEQUENCE_THEME's
// card-based visual language, not Workspace/Mail Merge's ribbon-strip one
// (a deliberate Phase 1 call, not an oversight — see that phase's notes),
// so this stays theme-agnostic and just needs the caller's palette to have
// the same key names (accent/accentMuted/red/redMuted/textSub), which every
// theme in workspaceTheme.js already does. Falls back to WORKSPACE_THEME
// when no theme is passed, matching this file's other components.
export function SelectionStateBar({count,total,onSelectAll,onClear,actions=[],theme,label="selected"}){
  const C2=theme||C;
  if(!count)return null;
  return(
    <div style={{display:"flex",alignItems:"center",gap:10,padding:"8px 14px",background:C2.accentMuted,borderRadius:8,marginBottom:10,flexWrap:"wrap"}}>
      <span style={{fontSize:12,fontWeight:600,color:C2.accent}}>{count} {label}</span>
      {typeof total==="number"&&onSelectAll&&count<total&&(
        <button onClick={onSelectAll} style={{background:"none",border:"none",color:C2.accent,fontSize:12,cursor:"pointer",textDecoration:"underline",padding:0}}>Select all {total}</button>
      )}
      <div style={{display:"flex",gap:8,marginLeft:"auto",flexWrap:"wrap"}}>
        {actions.map((a,i)=>(
          <button key={i} onClick={a.onClick} disabled={a.disabled} title={a.title}
            style={{background:a.danger?C2.redMuted:"#fff",color:a.danger?C2.red:C2.accent,border:"1px solid "+(a.danger?C2.red:C2.accent)+"55",borderRadius:6,padding:"5px 12px",fontSize:12,fontWeight:600,cursor:a.disabled?"not-allowed":"pointer",opacity:a.disabled?0.5:1}}>
            {a.label}
          </button>
        ))}
        <button onClick={onClear} style={{background:"none",border:"none",color:C2.textSub,fontSize:12,cursor:"pointer",padding:"5px 4px"}}>Clear</button>
      </div>
    </div>
  );
}

// Responsive ribbon overflow: wraps a tab's row of <RGroup> children and,
// when they don't all fit, moves the trailing ones behind a ">>" button that
// opens a dropdown containing those exact same groups — nothing is removed
// or duplicated, just relocated when there isn't room. At full width (the
// common case) every group renders exactly as it always did, in the same DOM
// position, so this changes nothing when the window is wide enough.
export function RibbonOverflowRow({children}){
  const containerRef=useRef(null);
  const measureRefs=useRef([]);
  const overflowWrapperRef=useRef(null);
  const [visibleCount,setVisibleCount]=useState(null); // null = not measured yet, show everything
  const [showOverflow,setShowOverflow]=useState(false);
  const [menuPos,setMenuPos]=useState(null); // viewport-relative {top,right}, computed on open
  const items=(Array.isArray(children)?children.filter(Boolean):(children?[children]:[]))
    .map((child,i)=>cloneElement(child,{key:"rov-"+i}));

  useLayoutEffect(()=>{
    // useLayoutEffect (not useEffect) so the very first measurement happens
    // synchronously before paint — avoids a flash-of-everything on mount
    // that would otherwise require clipping the container to hide.
    const recompute=()=>{
      const container=containerRef.current;
      if(!container)return;
      const widths=measureRefs.current.map(el=>el?el.offsetWidth:0);
      const available=container.clientWidth-40; // reserve room for the overflow button itself
      let total=0,count=0;
      for(let i=0;i<widths.length;i++){
        total+=widths[i];
        if(total>available&&i>0){break;}
        count=i+1;
      }
      setVisibleCount(Math.min(count,items.length));
    };
    recompute();
    const ro=new ResizeObserver(recompute);
    if(containerRef.current)ro.observe(containerRef.current);
    window.addEventListener("resize",recompute);
    return ()=>{ro.disconnect();window.removeEventListener("resize",recompute);};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[items.length]);

  useLayoutEffect(()=>{
    // The overflow flyout is rendered through a portal straight into
    // document.body (see below) so the ribbon strip's own scroll/clip
    // behavior (overflowX:"auto", which forces a matching overflow-y per
    // the CSS overflow spec) can't truncate it. Being portaled means it's
    // no longer positioned via CSS relative to the button, so its
    // coordinates are computed here instead, from the button's own
    // on-screen position, and kept in viewport ("fixed") terms.
    if(!showOverflow)return;
    const recomputeMenuPos=()=>{
      const el=overflowWrapperRef.current;
      if(!el)return;
      const r=el.getBoundingClientRect();
      setMenuPos({top:r.bottom,right:window.innerWidth-r.right});
    };
    recomputeMenuPos();
    const closeOnScroll=()=>setShowOverflow(false);
    window.addEventListener("resize",recomputeMenuPos);
    window.addEventListener("scroll",closeOnScroll,true);
    return ()=>{
      window.removeEventListener("resize",recomputeMenuPos);
      window.removeEventListener("scroll",closeOnScroll,true);
    };
  },[showOverflow]);

  const effectiveVisible=visibleCount===null?items.length:visibleCount;
  const hidden=items.slice(effectiveVisible);

  return(
    // NOTE: intentionally no `overflow:"hidden"` here — this row hosts every
    // existing ribbon dropdown (New Items, Move, Categorize, Schedule, etc.),
    // which render as position:absolute popups taller than the ribbon strip
    // itself. Clipping the container would silently truncate all of them.
    // Groups that don't fit are simply never rendered here (they move into
    // the overflow flyout below), so no clipping is needed to hide them.
    <div ref={containerRef} style={{display:"flex",flex:1,position:"relative",minWidth:0}}>
      {/* Hidden measurement layer — always renders every group at natural
          width off-screen, so widths for currently-collapsed groups stay
          known and they can reappear the moment there's room again. */}
      <div aria-hidden="true" style={{position:"absolute",top:-9999,left:-9999,display:"flex",visibility:"hidden",pointerEvents:"none"}}>
        {items.map((child,i)=>(
          <div key={i} ref={el=>{measureRefs.current[i]=el;}} style={{display:"flex"}}>{child}</div>
        ))}
      </div>
      {items.slice(0,effectiveVisible)}
      {hidden.length>0&&(
        <div ref={overflowWrapperRef} style={{position:"relative",display:"flex",alignItems:"stretch"}}>
          <button onClick={()=>setShowOverflow(v=>!v)} title="More commands" aria-label="More commands"
            style={{background:showOverflow?C.ribbonActive:"transparent",border:"none",borderLeft:"1px solid "+C.ribbonBorder+"99",borderRadius:R.sm,padding:"0 8px",cursor:"pointer",color:C.textSub,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,transition:"background .12s"}}>
            &raquo;
          </button>
          {showOverflow&&menuPos&&createPortal(
            /* Rendered via a portal directly under <body> — NOT as a normal
               descendant of the ribbon strip — because the ribbon content
               row above has overflowX:"auto", which (per the CSS overflow
               spec) forces a matching overflow-y:auto even though only the
               X axis was set. That silently clips any in-DOM dropdown
               taller than the ~70px ribbon strip. Portaling escapes that
               clipping entirely; position is computed in viewport ("fixed")
               terms in the effect above rather than via CSS relative
               positioning, since a portaled element inherits no positioning
               context from its logical (as opposed to DOM) parent.
               z-index here also has to clear 99999, not just other in-app
               popups' 9998/9999 — a standalone popup-window route wraps this
               entire component in its own position:fixed,zIndex:99999
               full-screen layer. Every other popup in the host file lives
               INSIDE that layer, so 9999 is high enough there; this one
               lives OUTSIDE it (in document.body), so it's compared against
               that layer directly and needs to outrank it or the opaque app
               shell paints over it. */
            <>
              <div onClick={()=>setShowOverflow(false)} style={{position:"fixed",inset:0,zIndex:100000}}/>
              <div onClick={e=>e.stopPropagation()} style={{position:"fixed",top:menuPos.top+6,right:menuPos.right,zIndex:100001,background:"#fff",border:"1px solid "+C.ribbonBorder,borderRadius:R.lg,boxShadow:"0 6px 24px rgba(0,0,0,.16)",display:"flex",padding:"4px 8px",flexWrap:"wrap",maxWidth:520}}>
                {hidden}
              </div>
            </>,
            document.body
          )}
        </div>
      )}
    </div>
  );
}
